'use client';

import { isAppWebView } from '@/components/analytics/analytics-host';
import { decryptAES128, encryptAES128 } from '@/lib/crypto-utils';
import { useAcademyListStore } from '@/stores/academy/academy-list.store';
import { useMemberStore } from '@/stores/member.store';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

function getEncryptedCoordFromSearch(
  source: { get: (name: string) => string | null },
  encryptedKey: string,
  rawKey: string,
): string {
  const encrypted = source.get(encryptedKey);
  if (encrypted) return encrypted;
  const raw = source.get(rawKey);
  return (raw ? encryptAES128(raw) : null) ?? '';
}

/**
 * 앱 웹뷰 진입 시 URL에 실린 브리지 파라미터를 member / academy-list 스토어에 반영합니다.
 * `/academy` 초기화와 동일한 해석(위치·면허·해시·버전)으로, 다른 탭(/mypage, /exam)에서도 컨텍스트가 유지되게 합니다.
 * 값이 있는 키만 갱신하되, `version`은 쿼리 키가 명시되면 빈 문자열도 그대로 반영합니다.
 */
export function useSyncAppWebContextFromSearchParams() {
  const searchParams = useSearchParams();
  const searchKey = searchParams.toString();

  useEffect(() => {
    const hash = searchParams.get('hash')?.trim();
    if (hash) {
      useMemberStore.getState().setHash(hash);
    }

    const version = searchParams.get('version');
    if (version !== null) {
      useMemberStore.getState().setVersion(version.trim());
    }

    const license = searchParams.get('license')?.trim();
    if (license) {
      useMemberStore.getState().setLicense(license);
    }

    // 쿼리가 빠진 채 새로 열린 앱 문서도 잡는다 — layout 인라인 스크립트가 브리지·세션 기록으로
    // 새긴 `data-app` 을 함께 본다
    const isApp =
      searchParams.get('isApp')?.toLowerCase() === 'true' || isAppWebView();
    const member = useMemberStore.getState();
    if (!member.isAppConnected && isApp) {
      member.setIsAppConnected(true);
    }

    const urlEncryptLat = getEncryptedCoordFromSearch(
      searchParams,
      'encryptLatitude',
      'latitude',
    );
    const urlEncryptLng = getEncryptedCoordFromSearch(
      searchParams,
      'encryptLongitude',
      'longitude',
    );
    if (urlEncryptLat && urlEncryptLng) {
      useAcademyListStore.getState().setCenterLocation({
        encryptLatitude: urlEncryptLat,
        encryptLongitude: urlEncryptLng,
      });
    }

    const encryptLocation = getEncryptedCoordFromSearch(
      searchParams,
      'encryptLocation',
      'location',
    );
    if (encryptLocation) {
      const decryptLocation = decryptAES128(encryptLocation) ?? '';
      if (decryptLocation) {
        useAcademyListStore.getState().setSearch(decryptLocation);
      }
    }
  }, [searchKey]);
}
